/**
 * RequireAuth — wraps protected routes (BUILD_SPEC.md §10.1).
 *
 * On mount, if the auth store has no token, attempt one refresh — the
 * httpOnly refresh cookie may still be valid even though the in-memory
 * token was lost (e.g. after a hard reload). If refresh fails, redirect
 * to /login while remembering the intended destination so we can land
 * the user back where they were after sign-in.
 */

import { type ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { proactiveRefresh } from "@/api/client";
import { meRequest } from "@/api/auth";

interface Props {
  children: ReactNode;
}

type AuthCheck = "checking" | "ok" | "fail";

export function RequireAuth({ children }: Props) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const location = useLocation();
  const [status, setStatus] = useState<AuthCheck>(accessToken && user ? "ok" : "checking");

  useEffect(() => {
    if (accessToken && user) {
      setStatus("ok");
      return;
    }
    let cancelled = false;
    (async () => {
      const ok = await proactiveRefresh();
      if (cancelled) return;
      if (!ok) {
        setStatus("fail");
        return;
      }
      // We have a fresh token but no cached user yet — fetch it.
      try {
        const fetchedUser = await meRequest();
        if (cancelled) return;
        // Read the latest token from the store (set by proactiveRefresh).
        const latest = useAuthStore.getState().accessToken;
        if (latest) {
          setSession({ accessToken: latest, user: fetchedUser });
        }
        setStatus("ok");
      } catch {
        if (!cancelled) setStatus("fail");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, user, setSession]);

  if (status === "checking") {
    return null; // Brief flash while we decide; <200ms in practice.
  }
  if (status === "fail") {
    // Preserve where the user wanted to go via state, so a successful
    // login can return them there (we don't read this in M4 yet, but
    // wiring it costs nothing).
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}
